// FP3-081 — Family-name sort key helper.
//
// Product rule: sort students alphabetically by FAMILY NAME (last name),
// not display name. For "Adelina Ramos Martinez" the sort key is "Ramos"
// (the first family-name token), not "Martinez".
//
// Priority order for deriving the sort key from a student record:
//   1. If `last_name` is present + non-empty, take its FIRST
//      whitespace-separated token. ("Ramos Martinez" → "ramos".)
//   2. Otherwise fall back to any full-name field
//      (`name` / `full_name` / `display_name`). If it contains a
//      comma, take the text before the comma and use its first token
//      ("Ramos Martinez, Adelina" → "ramos"). Otherwise split on
//      whitespace and take the SECOND token — index 1 skips the first
//      name. ("First Last" → "last", "First Last1 Last2" → "last1".)
//      If only one word is present, use that word.
//   3. Empty string means the row sorts last with `""` under localeCompare.
//
// All keys are trimmed + lowercased so sorting is case-insensitive.
// This function NEVER mutates the student record.

const _pickFirstToken = (s) =>
  (s || "").trim().split(/\s+/).filter(Boolean)[0] || "";

const _pickFamilyFromFullName = (fullName) => {
  const raw = (fullName || "").trim();
  if (!raw) return "";
  if (raw.includes(",")) {
    // "Last, First" or "Last1 Last2, First" — take text before comma,
    // then first token.
    return _pickFirstToken(raw.split(",", 2)[0]);
  }
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "";
  if (tokens.length === 1) return tokens[0];
  // "First Last" → tokens[1] ("last")
  // "First Last1 Last2" → tokens[1] ("last1") — first family-name token
  return tokens[1];
};

/**
 * Returns the lowercase family-name sort key for a student.
 * Pure function; does not mutate `student`.
 */
export function familyNameSortKey(student) {
  if (!student) return "";
  const ln = (student.last_name || "").trim();
  if (ln) {
    return _pickFirstToken(ln).toLowerCase();
  }
  const fallback =
    student.name || student.full_name || student.display_name || "";
  return _pickFamilyFromFullName(fallback).toLowerCase();
}

/**
 * Returns the lowercase given-name sort key (first name) for the
 * secondary sort tie-break. Prefers structured `first_name`; falls
 * back to first token of any full name.
 */
export function givenNameSortKey(student) {
  if (!student) return "";
  const fn = (student.first_name || "").trim();
  if (fn) return fn.toLowerCase();
  const raw =
    student.name || student.full_name || student.display_name || "";
  if (!raw) return "";
  if (raw.includes(",")) {
    // "Last, First …" → take first token after the comma.
    return _pickFirstToken(raw.split(",", 2)[1] || "").toLowerCase();
  }
  return _pickFirstToken(raw).toLowerCase();
}

/**
 * Case-insensitive family-name comparator with given-name tie-break.
 * Direction: "asc" (default) or "desc".
 *
 * Usage:
 *   students.slice().sort(compareByFamilyName())        // A → Z
 *   students.slice().sort(compareByFamilyName("desc"))  // Z → A
 */
export function compareByFamilyName(direction = "asc") {
  const sign = direction === "desc" ? -1 : 1;
  return (a, b) => {
    const cmp =
      familyNameSortKey(a).localeCompare(familyNameSortKey(b)) ||
      givenNameSortKey(a).localeCompare(givenNameSortKey(b));
    return cmp * sign;
  };
}
